import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { MessagingContact, MessagingPurpose } from "@/contracts";

import { revokeConsentMutation } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";

interface RevokeConsentDialogProps {
  contact: MessagingContact;
  purpose: MessagingPurpose;
  onClose: () => void;
}

/**
 * Withdrawing consent.
 *
 * Phrased as a removal from the list rather than a deletion, because that is
 * what happens: the consent record stays, since it is the evidence that the
 * permission existed and that it was withdrawn when asked.
 */
export function RevokeConsentDialog({ contact, purpose, onClose }: RevokeConsentDialogProps) {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");

  const mutation = useMutation({
    ...revokeConsentMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["messaging-contacts"] });
    },
  });

  async function submit() {
    await mutation.mutateAsync({
      id: contact.id,
      body: {
        purpose,
        ...(reason.trim().length > 0 ? { reason: reason.trim() } : {}),
      },
    });

    onClose();
  }

  return (
    <FormDialog
      open
      title={t("dialogRevokeTitle")}
      description={`${contact.displayName} — ${contact.phone}`}
      submitLabel={t("withdrawConsent")}
      destructive
      pending={mutation.isPending}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      onCancel={onClose}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        <TextField
          label={t("fieldReason")}
          hint={t("reasonHint")}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />

        <Alert tone="info" title={t("dialogRevokeRecordTitle")} announce={false}>
          {t("dialogRevokeRecordBody")}
        </Alert>
      </div>
    </FormDialog>
  );
}
